import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useToast } from '../contexts/ToastContext';
import { useTheme } from '../contexts/ThemeContext';
import { ApiClient } from '../utils/apiClient';
import { useWizardSessionManager } from '../utils/wizardSessionManager';
import { urlValidationService } from '../services/urlValidationService';
import URLValidationInput from './URLValidationInput';

const ALLOWED_FILE_TYPES = {
    'application/pdf': '.pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
    'text/plain': '.txt',
    'text/html': '.html'
};

const MAX_FILE_SIZE = 4 * 1024 * 1024; // 4MB in bytes

/**
 * Enhanced Knowledge Base Configuration Component
 * Implements session persistence, real-time URL validation, and progress indicators
 * Requirements: 1.1, 1.2, 1.3, 2.1, 2.2, 6.1, 6.2, 6.3, 6.4
 */
const KnowledgeBaseConfiguration = ({
    sessionId,
    parrotId,
    useKnowledgeBase = false,
    knowledgeBase = { url: '', files: [], textContent: [] },
    onToggle,
    onChange,
    disabled = false,
    className = '',
    hideToggle = false // New prop to hide the internal toggle when using dedicated toggle component
}) => {
    const { getToken } = useAuth();
    const { showSuccess, showError, showWarning } = useToast();
    const { theme } = useTheme();
    const apiClient = useRef(new ApiClient(getToken)).current;

    // Initialize session manager with KB support
    const sessionManager = useWizardSessionManager(sessionId, parrotId, getToken);

    // Local state management
    const [localState, setLocalState] = useState({
        useKnowledgeBase,
        selectedFiles: knowledgeBase.files || [],
        url: knowledgeBase.url || '',
        textContent: knowledgeBase.textContent || []
    });

    const [availableFiles, setAvailableFiles] = useState([]);
    const [loadingFiles, setLoadingFiles] = useState(false);

    // File upload state with progress tracking
    const fileInputRef = useRef(null);
    const [dragActive, setDragActive] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({});
    const [uploadErrors, setUploadErrors] = useState({});

    // URL validation state - now handled by URLValidationInput component
    const userInteractedWithUrlRef = useRef(false);
    const componentMountedRef = useRef(false);

    // Text content state
    const [showTextCreator, setShowTextCreator] = useState(false);
    const [textForm, setTextForm] = useState({ name: '', content: '' });
    const [textValidation, setTextValidation] = useState({});

    // Session sync state
    const [syncStatus, setSyncStatus] = useState('idle'); // idle, syncing, synced, error
    const [lastSyncTime, setLastSyncTime] = useState(null);

    // Load available files and session data on mount
    useEffect(() => {
        componentMountedRef.current = true;
        loadAvailableFiles();
        loadSessionData();
    }, []);

    // Auto-sync with session when local state changes (only for creation mode with session)
    useEffect(() => {
        if (sessionManager && sessionManager.sessionId && localState.useKnowledgeBase) {
            syncWithSession();
        }
    }, [localState.useKnowledgeBase, localState.selectedFiles, localState.url, localState.textContent, sessionManager]); // More specific dependencies to prevent infinite loop

    // Update local state when props change (but don't override session data)
    useEffect(() => {
        console.log('🔄 KnowledgeBaseConfiguration: Props changed, updating local state:', {
            useKnowledgeBase,
            currentLocalState: localState.useKnowledgeBase,
            willUpdate: useKnowledgeBase !== localState.useKnowledgeBase
        });
        
        setLocalState(prevState => {
            // Don't override URL if session data has already loaded a URL and props URL is empty
            const shouldKeepExistingUrl = prevState.url && prevState.url.trim() && (!knowledgeBase.url || !knowledgeBase.url.trim());
            
            return {
                ...prevState,
                useKnowledgeBase,
                selectedFiles: knowledgeBase.files || [],
                url: shouldKeepExistingUrl ? prevState.url : (knowledgeBase.url || ''),
                textContent: knowledgeBase.textContent || []
            };
        });

        // URL validation is now handled by URLValidationInput component
    }, [useKnowledgeBase, knowledgeBase]); // Removed parrotId from dependencies to prevent infinite loop

    /**
     * Load session data from session manager
     * Requirement: 1.1, 1.2 - Session persistence
     */
    const loadSessionData = useCallback(async () => {
        try {
            if (!sessionManager) return;

            // For editing mode, try to load existing parrot knowledge base data
            // Requirements: 12.1, 12.2, 12.3 - Pre-populate existing knowledge base data
            if (parrotId && !sessionManager.sessionId) {
                console.log('Loading existing parrot knowledge base data for editing mode');
                try {
                    const response = await apiClient.get(`/api/parrots/${parrotId}/wizard-data`);
                    if (response.success && response.wizard_data?.step1?.knowledge_base) {
                        const kbData = response.wizard_data.step1.knowledge_base;
                        
                        // Convert API format to component format
                        const convertedFiles = (kbData.files || []).map(file => ({
                            id: file.id,
                            name: file.name || file.original_filename,
                            size: file.size || file.file_size,
                            type: file.type || 'file',
                            mime_type: file.mime_type,
                            sync_status: file.sync_status || 'synced',
                            selected: true, // Mark as selected for editing
                            existing: true, // Mark as existing file
                            created_at: file.created_at,
                            updated_at: file.updated_at
                        }));

                        const convertedTextContent = (kbData.text_documents || []).map(doc => ({
                            id: doc.id,
                            name: doc.name,
                            content: doc.content_text || doc.content,
                            sync_status: doc.sync_status || 'synced',
                            selected: true, // Mark as selected for editing
                            existing: true, // Mark as existing document
                            created_at: doc.created_at,
                            updated_at: doc.updated_at
                        }));

                        setLocalState(prevState => ({
                            ...prevState,
                            selectedFiles: convertedFiles,
                            url: kbData.url || '',
                            textContent: convertedTextContent
                            // Don't override useKnowledgeBase - it should be controlled by the parent prop
                        }));
                        
                        console.log('🔧 Fixed: Not overriding useKnowledgeBase in loadSessionData:', {
                            hasContent: convertedFiles.length > 0 || kbData.url || convertedTextContent.length > 0,
                            keepingParentProp: true,
                            currentUseKnowledgeBase: 'controlled by parent prop'
                        });

                        console.log('Pre-populated knowledge base data for editing:', {
                            filesCount: convertedFiles.length,
                            hasUrl: !!kbData.url,
                            textContentCount: convertedTextContent.length
                        });

                        return;
                    }
                } catch (error) {
                    console.warn('Failed to load existing parrot KB data, falling back to session data:', error);
                }
            }

            // Fallback to session data
            const kbConfig = sessionManager.getKnowledgeBaseConfig();
            if (kbConfig) {
                setLocalState(prevState => ({
                    ...prevState,
                    selectedFiles: kbConfig.files || [],
                    url: kbConfig.urls?.[0]?.url || '',
                    textContent: kbConfig.textContent || []
                }));

                // URL validation is now handled by URLValidationInput component
            }
        } catch (error) {
            console.error('Failed to load session data:', error);
        }
    }, [sessionManager, parrotId, apiClient]);

    /**
     * Sync local state with session manager
     * Requirement: 1.1, 1.2, 1.3 - Session persistence
     */
    const syncWithSession = useCallback(async () => {
        try {
            if (!sessionManager || !localState.useKnowledgeBase) return;

            // For editing mode (no session ID), just save locally without backend sync
            if (!sessionManager.sessionId) {
                console.log('Editing mode: saving KB config locally without backend sync');
                const kbConfig = {
                    files: localState.selectedFiles,
                    urls: localState.url ? [{
                        id: `url_${Date.now()}`,
                        url: localState.url,
                        status: 'validated' // URLs are validated by URLValidationInput before being set
                    }] : [],
                    textContent: localState.textContent,
                    metadata: {
                        last_updated: new Date().toISOString(),
                        total_files: localState.selectedFiles.length,
                        total_urls: localState.url ? 1 : 0,
                        total_text_items: localState.textContent.length
                    }
                };
                
                sessionManager.saveKnowledgeBaseConfig(kbConfig);
                setSyncStatus('synced');
                setLastSyncTime(new Date());
                return;
            }

            // Update session with current KB configuration (local only)
            const kbConfig = {
                files: localState.selectedFiles,
                urls: localState.url ? [{
                    id: `url_${Date.now()}`,
                    url: localState.url,
                    status: 'validated' // URLs are validated by URLValidationInput before being set
                }] : [],
                textContent: localState.textContent,
                metadata: {
                    last_updated: new Date().toISOString(),
                    total_files: localState.selectedFiles.length,
                    total_urls: localState.url ? 1 : 0,
                    total_text_items: localState.textContent.length
                }
            };

            // IMPORTANT: Don't sync with backend during step1 - only save locally
            // Files will be queued for sync when user saves at step2 or step3
            console.log('KB configuration saved locally - will sync on step2/step3 save');
            
            sessionManager.saveKnowledgeBaseConfig(kbConfig);
            setSyncStatus('synced');
            setLastSyncTime(new Date());

        } catch (error) {
            console.error('Failed to sync with session:', error);
            setSyncStatus('error');
            showWarning('Failed to save knowledge base configuration to session');
        }
    }, [sessionManager, localState, showWarning]);

    /**
     * Load user's available knowledge base files
     * Requirement: 6.1, 6.2 - Progress indicators and feedback
     */
    const loadAvailableFiles = async () => {
        try {
            setLoadingFiles(true);
            const response = await apiClient.get('/api/knowledge-base/files');
            setAvailableFiles(response.data || response.files || []);
        } catch (error) {
            console.error('Failed to load available files:', error);
            showError('Failed to load available files');
        } finally {
            setLoadingFiles(false);
        }
    };

    /**
     * Toggle knowledge base usage with session persistence
     * Requirement: 1.1, 1.2 - Session persistence
     */
    const handleToggleKnowledgeBase = useCallback((enabled) => {
        const updatedState = {
            ...localState,
            useKnowledgeBase: enabled
        };

        setLocalState(updatedState);

        if (onToggle) onToggle(enabled);

        if (enabled) {
            // Load session data when enabling
            loadSessionData();
        }

        // Notify parent component
        if (onChange) {
            onChange({
                url: enabled ? updatedState.url : '',
                files: enabled ? updatedState.selectedFiles : [],
                textContent: enabled ? updatedState.textContent : []
            });
        }
    }, [localState, onToggle, onChange]); // Removed loadSessionData from dependencies to prevent infinite loop

    /**
     * Handle file selection with session persistence
     * Requirement: 1.1, 1.2, 1.3 - Session persistence
     */
    const handleFileSelection = useCallback(async (fileId, selected) => {
        try {
            const updatedFiles = selected
                ? [...localState.selectedFiles, availableFiles.find(f => f.id === fileId)]
                : localState.selectedFiles.filter(f => f.id !== fileId);

            const updatedState = { ...localState, selectedFiles: updatedFiles };
            setLocalState(updatedState);

            // Update session manager
            if (sessionManager && selected) {
                const file = availableFiles.find(f => f.id === fileId);
                if (file) {
                    await sessionManager.addFile({
                        name: file.name,
                        size: file.size,
                        type: file.type,
                        r2_key: file.r2_key,
                        status: 'selected'
                    });
                    
                    // IMPORTANT: Don't assign files immediately during upload/selection
                    // Files will be assigned when user saves at Step 2 or Step 3
                    console.log(`File ${fileId} selected for parrot ${parrotId} - assignment will happen on save`);
                }
            } else if (sessionManager && !selected) {
                sessionManager.removeFile(fileId);
                
                // For editing mode, explicitly unassign the file from the parrot
                if (parrotId && !sessionManager.sessionId) {
                    try {
                        await apiClient.post(`/api/parrots/${parrotId}/knowledge-base/${fileId}/unassign`);
                        console.log(`File ${fileId} unassigned from parrot ${parrotId}`);
                    } catch (error) {
                        console.error('Failed to unassign file from parrot:', error);
                        showError(`Failed to unassign file from parrot`);
                    }
                }
            }

            if (onChange) {
                onChange({
                    url: updatedState.url,
                    files: updatedFiles,
                    textContent: updatedState.textContent
                });
            }
        } catch (error) {
            console.error('Failed to handle file selection:', error);
            showError('Failed to update file selection');
        }
    }, [localState, availableFiles, sessionManager, onChange, showError]); // Note: Keeping all dependencies as they are needed for the function to work correctly

    /**
     * Validate file type and size
     * Requirement: 6.3, 6.4 - Error handling
     */
    const validateFile = (file) => {
        if (!ALLOWED_FILE_TYPES[file.type]) {
            return {
                valid: false,
                error: 'Only PDF, DOCX, TXT, and HTML files are supported'
            };
        }
        if (file.size > MAX_FILE_SIZE) {
            return {
                valid: false,
                error: 'File size must be under 4MB'
            };
        }
        return { valid: true };
    };

    /**
     * Handle file upload with progress tracking and session integration
     * Requirement: 1.1, 1.2, 1.3, 6.1, 6.2, 6.3, 6.4 - File upload with session storage and progress indicators
     */
    const handleFileUpload = useCallback(async (files) => {
        if (!files || files.length === 0) return;

        setUploading(true);
        const uploadResults = [];
        const errors = {};

        try {
            for (const file of files) {
                const validation = validateFile(file);
                if (!validation.valid) {
                    errors[file.name] = validation.error;
                    showError(`${file.name}: ${validation.error}`);
                    continue;
                }

                const fileId = `upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
                setUploadProgress(prev => ({
                    ...prev,
                    [fileId]: { progress: 0, file, status: 'uploading' }
                }));

                try {
                    const formData = new FormData();
                    formData.append('file', file);

                    // Simulate progress tracking
                    const progressInterval = setInterval(() => {
                        setUploadProgress(prev => ({
                            ...prev,
                            [fileId]: {
                                ...prev[fileId],
                                progress: Math.min(prev[fileId]?.progress + 10, 90)
                            }
                        }));
                    }, 200);

                    const response = await apiClient.post('/api/knowledge-base', formData);

                    clearInterval(progressInterval);
                    setUploadProgress(prev => ({
                        ...prev,
                        [fileId]: {
                            ...prev[fileId],
                            progress: 100,
                            status: 'completed'
                        }
                    }));

                    uploadResults.push(response.data);

                    // Add to session manager with id so it persists/selects correctly
                    if (sessionManager) {
                        await sessionManager.addFile({
                            id: response.data.id,
                            name: response.data.name,
                            size: response.data.size,
                            type: response.data.mime_type || response.data.type || 'application/octet-stream',
                            r2_key: response.data.r2_key || '',
                            status: 'uploaded'
                        });
                        
                        // IMPORTANT: Don't assign files immediately during upload/selection
                        // Files will be assigned when user saves at Step 2 or Step 3
                        console.log(`File ${response.data.id} uploaded for parrot ${parrotId} - assignment will happen on save`);
                    }

                    showSuccess(`Successfully uploaded ${file.name}`);
                } catch (error) {
                    console.error('File upload failed:', error);

                    setUploadProgress(prev => ({
                        ...prev,
                        [fileId]: {
                            ...prev[fileId],
                            status: 'error',
                            error: error.message
                        }
                    }));

                    errors[file.name] = error.message || 'Upload failed';
                    showError(`Failed to upload ${file.name}: ${error.message || 'Upload failed'}`);
                }
            }

            setUploadErrors(errors);

            // Invalidate cached KB lists before refreshing to avoid stale results
            try {
                apiClient.invalidateCaches?.([
                    '/api/knowledge-base',
                    '/api/knowledge-base/files'
                ]);
            } catch (_) {}

            // Refresh available files and auto-select uploaded files
            await loadAvailableFiles();

            if (uploadResults.length > 0) {
                // Normalize uploaded results to the shape used in UI
                const normalized = uploadResults.map(f => ({
                    id: f.id,
                    name: f.name,
                    type: f.type || 'file',
                    mime_type: f.mime_type,
                    file_path: f.r2_key || '',
                    file_size: typeof f.size === 'number' ? f.size : 0,
                    sync_status: f.sync_status || 'not_assigned'
                }));

                const updatedSelectedFiles = [...localState.selectedFiles, ...normalized];
                const updatedState = { ...localState, selectedFiles: updatedSelectedFiles };
                setLocalState(updatedState);

                if (onChange) {
                    onChange({
                        url: updatedState.url,
                        files: updatedSelectedFiles,
                        textContent: updatedState.textContent
                    });
                }
            }
        } finally {
            setUploading(false);
            // Clear upload progress after delay
            setTimeout(() => {
                setUploadProgress({});
                setUploadErrors({});
            }, 3000);
        }
    }, [localState, apiClient, sessionManager, showSuccess, showError, onChange, loadAvailableFiles]);



    /**
     * Handle URL input with real-time validation
     * Requirement: 2.1, 2.2 - URL validation and storage
     */
    const handleUrlChange = useCallback((url) => {
        // Mark that user has interacted with the URL input
        userInteractedWithUrlRef.current = true;
        
        const updatedState = { ...localState, url };
        setLocalState(updatedState);

        // Only trigger validation if URL is not blank
        if (url && url.trim()) {
            validateUrlWithFeedback(url);
        } else {
            // Explicitly clear validation for blank URLs
            setUrlValidation({ status: 'idle', message: '', isValidating: false });
        }

        if (onChange) {
            onChange({
                url,
                files: updatedState.selectedFiles,
                textContent: updatedState.textContent
            });
        }
    }, [localState, onChange]);

    /**
     * Handle URL validation success from URLValidationInput component
     * Requirement: 13.3, 13.4 - Verified URL handling
     */
    const handleUrlValidated = useCallback(async (urlData) => {
        try {
            const updatedState = { ...localState, url: urlData.url };
            setLocalState(updatedState);

            // Update session manager with validated URL
            if (sessionManager) {
                await sessionManager.addUrl(urlData.url);
            }

            // Notify parent component
            if (onChange) {
                onChange({
                    url: urlData.url,
                    files: updatedState.selectedFiles,
                    textContent: updatedState.textContent
                });
            }

            showSuccess('URL validated and added successfully');
        } catch (error) {
            console.error('Failed to handle validated URL:', error);
            showError('Failed to save validated URL');
        }
    }, [localState, sessionManager, onChange, showSuccess, showError]);

    /**
     * Handle URL removal from URLValidationInput component
     * Requirement: 13.6, 13.7 - URL removal functionality
     */
    const handleUrlRemoved = useCallback(async () => {
        try {
            const updatedState = { ...localState, url: '' };
            setLocalState(updatedState);

            // Remove URL from session manager
            if (sessionManager) {
                sessionManager.removeUrl();
            }

            // Notify parent component
            if (onChange) {
                onChange({
                    url: '',
                    files: updatedState.selectedFiles,
                    textContent: updatedState.textContent
                });
            }

            showSuccess('URL removed successfully');
        } catch (error) {
            console.error('Failed to remove URL:', error);
            showError('Failed to remove URL');
        }
    }, [localState, sessionManager, onChange, showSuccess, showError]);

    /**
     * Handle text content creation with session integration
     * Requirement: 1.1, 1.2, 1.3 - Session persistence for text content
     */
    const handleTextContentSubmit = useCallback(async () => {
        // Validate text content
        const errors = {};
        if (!textForm.name.trim()) errors.name = 'Name is required';
        if (!textForm.content.trim()) errors.content = 'Content is required';
        if (textForm.content.length > 1000000) errors.content = 'Content is too long (max 1MB)';

        setTextValidation(errors);
        if (Object.keys(errors).length > 0) return;

        try {
            const response = await apiClient.post('/api/knowledge-base/text/create', {
                name: textForm.name,
                content: textForm.content
            });

            const textFile = response.file;

            // Add to session manager
            if (sessionManager) {
                sessionManager.addTextContent({
                    name: textForm.name,
                    content: textForm.content
                });
                
                // IMPORTANT: Don't assign files immediately during upload/selection
                // Files will be assigned when user saves at Step 2 or Step 3
                if (textFile && textFile.id) {
                    console.log(`Text content ${textFile.id} created for parrot ${parrotId} - assignment will happen on save`);
                }
            }

            showSuccess(`Text content "${textForm.name}" created successfully`);

            // Add to selected files
            const updatedSelectedFiles = [...localState.selectedFiles, textFile];
            const updatedState = { ...localState, selectedFiles: updatedSelectedFiles };
            setLocalState(updatedState);

            if (onChange) {
                onChange({
                    url: updatedState.url,
                    files: updatedSelectedFiles,
                    textContent: updatedState.textContent
                });
            }

            // Reset form and close creator
            setTextForm({ name: '', content: '' });
            setShowTextCreator(false);
            setTextValidation({});

            // Refresh available files
            await loadAvailableFiles();
        } catch (error) {
            console.error('Failed to create text content:', error);
            showError('Failed to create text content');
        }
    }, [textForm, localState, apiClient, sessionManager, showSuccess, showError, onChange, loadAvailableFiles]);

    /**
     * Drag and drop handlers
     */
    const handleDrag = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (disabled || !localState.useKnowledgeBase) return;

        const files = Array.from(e.dataTransfer.files);
        handleFileUpload(files);
    }, [disabled, localState.useKnowledgeBase, handleFileUpload]);

    const isDark = theme === 'dark';

    return (
        <div className={`knowledge-base-configuration ${className}`}>
            {/* Toggle Section - Only show if not hidden */}
            {!hideToggle && (
                <div className={`rounded-lg p-6 border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                    }`}>
                    <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3">
                            <div className="flex items-center h-5">
                                <input
                                    id="use-knowledge-base"
                                    type="checkbox"
                                    checked={localState.useKnowledgeBase}
                                    onChange={(e) => handleToggleKnowledgeBase(e.target.checked)}
                                    disabled={disabled}
                                    className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500 dark:border-gray-600"
                                />
                            </div>
                            <div className="flex-1">
                                <label
                                    htmlFor="use-knowledge-base"
                                    className={`text-sm font-medium cursor-pointer ${isDark ? 'text-white' : 'text-gray-900'
                                        }`}
                                >
                                    Use Knowledge Base For This Parrot
                                </label>
                                <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'
                                    }`}>
                                    Enable this to give your AI agent access to your uploaded files and website content
                                </p>
                            </div>
                        </div>

                        {/* Session sync status indicator */}
                        {localState.useKnowledgeBase && (
                            <div className="flex items-center space-x-2">
                                {syncStatus === 'syncing' && (
                                    <div className="flex items-center space-x-1 text-blue-600">
                                        <div className="animate-spin rounded-full h-3 w-3 border-b border-blue-600"></div>
                                        <span className="text-xs">Syncing...</span>
                                    </div>
                                )}
                                {syncStatus === 'synced' && lastSyncTime && (
                                    <div className="flex items-center space-x-1 text-green-600">
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        <span className="text-xs">Saved</span>
                                    </div>
                                )}
                                {syncStatus === 'error' && (
                                    <div className="flex items-center space-x-1 text-red-600">
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                        </svg>
                                        <span className="text-xs">Sync failed</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Session sync status indicator - Show when toggle is hidden */}
            {hideToggle && localState.useKnowledgeBase && (
                <div className="flex justify-end mb-4">
                    <div className="flex items-center space-x-2">
                        {syncStatus === 'syncing' && (
                            <div className="flex items-center space-x-1 text-blue-600">
                                <div className="animate-spin rounded-full h-3 w-3 border-b border-blue-600"></div>
                                <span className="text-xs">Syncing...</span>
                            </div>
                        )}
                        {syncStatus === 'synced' && lastSyncTime && (
                            <div className="flex items-center space-x-1 text-green-600">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <span className="text-xs">Saved</span>
                            </div>
                        )}
                        {syncStatus === 'error' && (
                            <div className="flex items-center space-x-1 text-red-600">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                    </svg>
                                    <span className="text-xs">Sync failed</span>
                                </div>
                            )}
                        </div>
                    </div>
            )}

            {/* Knowledge Base Configuration - Only show when enabled */}
            {console.log('🎯 KnowledgeBaseConfiguration conditional rendering:', { 
                'localState.useKnowledgeBase': localState.useKnowledgeBase,
                'useKnowledgeBase prop': useKnowledgeBase,
                'shouldShow': localState.useKnowledgeBase 
            }) || null}
            {localState.useKnowledgeBase && (
                <div className="mt-6 space-y-6">
                    {/* URL Input with Backend Validation */}
                    <div className={`rounded-lg p-6 border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                        }`}>
                        <h3 className={`text-lg font-medium mb-4 ${isDark ? 'text-white' : 'text-gray-900'
                            }`}>
                            Website URL
                        </h3>

                        <div className="space-y-3">
                            <URLValidationInput
                                value={localState.url}
                                onValidated={handleUrlValidated}
                                onRemove={handleUrlRemoved}
                                disabled={disabled}
                                placeholder="Enter URL (e.g., https://example.com/docs)"
                                className="w-full"
                            />

                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                Add one website URL for your parrot to reference (max 1 URL per parrot)
                            </p>
                        </div>
                    </div>

                    {/* File Selection Interface */}
                    <div className={`rounded-lg p-6 border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                        }`}>
                        <h3 className={`text-lg font-medium mb-4 ${isDark ? 'text-white' : 'text-gray-900'
                            }`}>
                            Select Files
                        </h3>

                        {loadingFiles ? (
                            <div className="flex items-center justify-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                                <span className={`ml-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                    Loading available files...
                                </span>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {availableFiles.length === 0 ? (
                                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                        No files available. Upload files below to get started.
                                    </p>
                                ) : (
                                    availableFiles
                                        .filter(file => file && file.id)
                                        .map((file) => (
                                            <div key={file.id} className={`flex items-center space-x-3 p-3 rounded border ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'
                                                }`}>
                                                <input
                                                    type="checkbox"
                                                    checked={localState.selectedFiles.some(f => f && f.id === file.id)}
                                                    onChange={(e) => handleFileSelection(file.id, e.target.checked)}
                                                    className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded dark:border-gray-600"
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'
                                                        }`}>
                                                        {file.originalName || file.name || 'Unnamed file'}
                                                    </p>
                                                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'
                                                        }`}>
                                                        {file.documentType === 'text' ? 'Text Content' : 'File'} •
                                                        {file.fileSize ? ` ${Math.round(file.fileSize / 1024)}KB` : ''}
                                                    </p>
                                                </div>
                                            </div>
                                        ))
                                )}
                            </div>
                        )}
                    </div>

                    {/* File Upload Component with Progress Tracking */}
                    <div className={`rounded-lg p-6 border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                        }`}>
                        <h3 className={`text-lg font-medium mb-4 ${isDark ? 'text-white' : 'text-gray-900'
                            }`}>
                            Upload New Files
                        </h3>

                        <div
                            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${dragActive
                                ? 'border-teal-400 bg-teal-50 dark:bg-teal-900/20'
                                : `border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 ${isDark ? 'bg-gray-700' : 'bg-gray-50'
                                }`
                                } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                            onDragEnter={handleDrag}
                            onDragLeave={handleDrag}
                            onDragOver={handleDrag}
                            onDrop={handleDrop}
                            onClick={() => !disabled && fileInputRef.current?.click()}
                        >
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                accept={Object.values(ALLOWED_FILE_TYPES).join(',')}
                                onChange={(e) => handleFileUpload(Array.from(e.target.files))}
                                disabled={disabled}
                                className="hidden"
                            />

                            <div className="space-y-2">
                                <div className={`mx-auto h-12 w-12 ${isDark ? 'text-gray-400' : 'text-gray-400'
                                    }`}>
                                    {uploading ? (
                                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
                                    ) : (
                                        <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                        </svg>
                                    )}
                                </div>
                                <div>
                                    <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                        <span className="font-medium text-teal-600 hover:text-teal-500">
                                            Click to upload
                                        </span> or drag and drop
                                    </p>
                                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                        PDF, DOCX, TXT, HTML files up to 4MB each
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Upload Progress Display */}
                        {Object.keys(uploadProgress).length > 0 && (
                            <div className="mt-4 space-y-3">
                                <h4 className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                    Upload Progress
                                </h4>
                                {Object.entries(uploadProgress).map(([fileId, { progress, file, status, error }]) => (
                                    <div key={fileId} className="space-y-2">
                                        <div className="flex justify-between items-center text-sm">
                                            <span className={`truncate flex-1 mr-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                                {file?.name || 'Unknown file'}
                                            </span>
                                            <div className="flex items-center space-x-2">
                                                {status === 'completed' && (
                                                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                )}
                                                {status === 'error' && (
                                                    <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                )}
                                                <span className={`text-xs ${status === 'completed' ? 'text-green-600' :
                                                    status === 'error' ? 'text-red-600' :
                                                        isDark ? 'text-gray-400' : 'text-gray-500'
                                                    }`}>
                                                    {status === 'completed' ? 'Complete' :
                                                        status === 'error' ? 'Failed' :
                                                            `${progress}%`}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                            <div
                                                className={`h-2 rounded-full transition-all duration-300 ${status === 'completed' ? 'bg-green-600' :
                                                    status === 'error' ? 'bg-red-600' :
                                                        'bg-teal-600'
                                                    }`}
                                                style={{ width: `${progress}%` }}
                                            />
                                        </div>
                                        {error && (
                                            <p className="text-xs text-red-600">{error}</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Text Content Creation Interface */}
                    <div className={`rounded-lg p-6 border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                        }`}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'
                                }`}>
                                Create Text Content
                            </h3>
                            <button
                                type="button"
                                onClick={() => setShowTextCreator(!showTextCreator)}
                                disabled={disabled}
                                className={`px-3 py-1 text-sm rounded-md border transition-colors ${isDark
                                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                {showTextCreator ? 'Cancel' : 'Add Text'}
                            </button>
                        </div>

                        {showTextCreator && (
                            <div className="space-y-4">
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'
                                        }`}>
                                        Name
                                    </label>
                                    <input
                                        type="text"
                                        value={textForm.name}
                                        onChange={(e) => setTextForm(prev => ({ ...prev, name: e.target.value }))}
                                        placeholder="e.g., Company Policies"
                                        className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${isDark
                                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                            } ${textValidation.name ? 'border-red-500' : ''}`}
                                    />
                                    {textValidation.name && (
                                        <p className="text-xs text-red-600 mt-1">{textValidation.name}</p>
                                    )}
                                </div>

                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'
                                        }`}>
                                        Content
                                    </label>
                                    <textarea
                                        value={textForm.content}
                                        onChange={(e) => setTextForm(prev => ({ ...prev, content: e.target.value }))}
                                        placeholder="Enter your text content here..."
                                        rows={6}
                                        className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${isDark
                                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                            } ${textValidation.content ? 'border-red-500' : ''}`}
                                    />
                                    {textValidation.content && (
                                        <p className="text-xs text-red-600 mt-1">{textValidation.content}</p>
                                    )}
                                    <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                        {textForm.content.length.toLocaleString()} / 1,000,000 characters
                                    </p>
                                </div>

                                <div className="flex space-x-3">
                                    <button
                                        type="button"
                                        onClick={handleTextContentSubmit}
                                        disabled={!textForm.name.trim() || !textForm.content.trim()}
                                        className="px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-md hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Create Text Content
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowTextCreator(false);
                                            setTextForm({ name: '', content: '' });
                                            setTextValidation({});
                                        }}
                                        className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors ${isDark
                                            ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                            : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                            }`}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}

                        {!showTextCreator && (
                            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                Create custom text content for your parrot to reference
                            </p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default KnowledgeBaseConfiguration;
